import React, { useState } from 'react';
import { useIdioma } from '../i18n/idioma';
import toast from 'react-hot-toast';
import { DollarSign, X, ArrowRight, SkipForward } from 'lucide-react';
import { useSettings } from '../contexts/SettingsContext';
import { getIndustryPricingDefaults } from '../data/industryPricingDefaults';
import { MAX_TARGET_MARGIN_PERCENT } from '../services/metrics';
import { Button } from './ui/Button';

interface QuickPricingSetupModalProps {
  isOpen: boolean;
  onComplete: () => void;
  onSkip: () => void;
}

const QuickPricingSetupModal: React.FC<QuickPricingSetupModalProps> = ({
  isOpen,
  onComplete,
  onSkip,
}) => {
    const { t } = useIdioma();
  const { settings, updateSettings, saveSettings } = useSettings();
  const industryDefaults = getIndustryPricingDefaults(settings.profile.industryType);

  /* `??`, NUNCA `||`.
   *
   * Un margen guardado en 0 es FALSY: con `||` este modal lo cambiaba por el
   * valor sugerido del oficio y `handleSave` lo guardaba. El contratista que
   * había puesto 0 % a propósito entraba solo a cambiar el cargo por viaje, y
   * al salir todos sus presupuestos subían un 38,9 % sin que nadie le avisara.
   * Lo mismo con un markup de 0 y con un cargo por viaje de 0 (que es una
   * respuesta legítima: «no cobro viaje»). */
  const [margin, setMargin] = useState(
    settings.pricing.targetMarginPercent ?? industryDefaults.targetMarginPercent
  );
  const [markup, setMarkup] = useState(
    settings.pricing.defaultMaterialMarkupPercent ?? industryDefaults.defaultMaterialMarkupPercent
  );
  const [tripCharge, setTripCharge] = useState(settings.rules.tripCharge ?? 15);
  const [saving, setSaving] = useState(false);

  /* Re-sembrar el formulario CADA VEZ que se abre.
   *
   * Los `useState` de arriba solo corren en el montaje del componente, que
   * ocurre al arrancar la app —antes de que `SettingsContext` termine de traer
   * los ajustes reales de Supabase—. Así que el modal se quedaba con los
   * valores de fábrica del gremio y, al pulsar Guardar, los escribía ENCIMA de
   * los del taller: un taller con 30/20/$0 veía 20/30/$15 y los persistía. El
   * kit de $20 pasaba de $24,00 a $26,00 y el margen objetivo de la mano de
   * obra bajaba de 30% a 20%, sin que nadie tecleara nada. */
  React.useEffect(() => {
    if (!isOpen) return;
    setMargin(settings.pricing.targetMarginPercent || industryDefaults.targetMarginPercent);
    setMarkup(settings.pricing.defaultMaterialMarkupPercent || industryDefaults.defaultMaterialMarkupPercent);
    setTripCharge(settings.rules.tripCharge || 15);
  }, [
    isOpen,
    settings.pricing.targetMarginPercent,
    settings.pricing.defaultMaterialMarkupPercent,
    settings.rules.tripCharge,
    industryDefaults.targetMarginPercent,
    industryDefaults.defaultMaterialMarkupPercent,
  ]);

  if (!isOpen) return null;

  const handleSave = async () => {
    setSaving(true);
    try {
      updateSettings({
        ...settings,
        pricing: {
          ...settings.pricing,
          targetMarginPercent: margin,
          defaultMaterialMarkupPercent: markup,
        },
        rules: {
          ...settings.rules,
          tripCharge,
        },
      });
      await saveSettings();
      toast.success(t('precios.toast.guardado'));
      onComplete();
    } catch {
      toast.error(t('precios.toast.error'));
    } finally {
      setSaving(false);
    }
  };

  const numberInputClass = "w-full h-10 px-3 bg-white border border-acero-300 rounded-xl text-navy-950 text-sm font-data tabular-nums text-right focus:outline-none focus:border-navy-600 focus:ring-2 focus:ring-brand-400/55 transition-[border-color,box-shadow] duration-150";

  return (
    <div className="fixed inset-0 bg-navy-950/50 z-[260] flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-s3 w-full max-w-md animate-in zoom-in-95 overflow-hidden flex flex-col max-h-[90dvh]">
        <div className="px-6 py-4 border-b border-acero-200 shrink-0">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-acero-100 text-navy-700 rounded-xl shrink-0">
                <DollarSign size={20} />
              </div>
              <div>
                <h3 className="text-lg font-semibold tracking-tight text-navy-950">{t('precios.modal.titulo')}</h3>
                <p className="text-sm text-acero-500 mt-0.5">
                  {t('precios.modal.subtitulo')}
                </p>
              </div>
            </div>
            <button
              onClick={onSkip}
              aria-label="Cerrar"
              className="p-2 rounded-lg text-peligro hover:bg-peligro-bg transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="px-6 py-5 space-y-4 overflow-y-auto custom-scrollbar flex-1 min-h-0">
          <div>
            <label className="label-ui block mb-1.5">
              {t('precios.campo.margen')}
            </label>
            {/* `min`/`max` en el HTML son una pista, no un límite: el usuario
                puede teclear 500 y el navegador lo acepta. Se recorta aquí, que
                es el único sitio de los cinco que tocan el margen donde no
                había ningún tope. */}
            <input
              type="number"
              min={0}
              max={MAX_TARGET_MARGIN_PERCENT}
              value={margin}
              onChange={e => setMargin(Math.min(Math.max(Number(e.target.value) || 0, 0), MAX_TARGET_MARGIN_PERCENT))}
              className={numberInputClass}
            />
            <p className="text-xs text-acero-500 mt-1.5">{t('precios.campo.margen_ayuda')}</p>
          </div>

          <div>
            <label className="label-ui block mb-1.5">
              {t('precios.campo.markup')}
            </label>
            <input
              type="number"
              min={0}
              max={200}
              value={markup}
              onChange={e => setMarkup(Number(e.target.value) || 0)}
              className={numberInputClass}
            />
            <p className="text-xs text-acero-500 mt-1.5">{t('precios.campo.markup_ayuda')}</p>
          </div>

          <div>
            <label className="label-ui block mb-1.5">
              {t('precios.campo.viaje')}
            </label>
            <input
              type="number"
              min={0}
              step={5}
              value={tripCharge}
              onChange={e => setTripCharge(Number(e.target.value) || 0)}
              className={numberInputClass}
            />
          </div>
        </div>

        <div className="px-6 py-4 border-t border-acero-200 bg-acero-50 flex flex-col gap-2 shrink-0">
          <Button
            variant="primary"
            className="w-full"
            onClick={handleSave}
            disabled={saving || margin <= 0}
            isLoading={saving}
            rightIcon={<ArrowRight size={16} />}
          >
            {saving ? t('precios.boton.guardando') : t('precios.boton.guardar')}
          </Button>
          <Button
            variant="ghost"
            className="w-full"
            onClick={onSkip}
            leftIcon={<SkipForward size={14} />}
          >
            {t('precios.boton.saltar')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default QuickPricingSetupModal;
